import React, { useRef, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { useI18n } from '../../i18n';

// Map language code → ISO 3166-1 alpha-2 country code for flagcdn.com
const FLAG_COUNTRY = {
  pl: 'pl',
  en: 'gb',
  de: 'de',
  sk: 'sk',
  cs: 'cz',
};

export function FlagImg({ code, size = 20 }) {
  const country = FLAG_COUNTRY[code] || code;
  return (
    <img
      src={`https://flagcdn.com/w40/${country}.png`}
      srcSet={`https://flagcdn.com/w80/${country}.png 2x`}
      width={size}
      height={Math.round(size * 0.75)}
      alt=""
      aria-hidden="true"
      className="rounded-sm object-cover flex-shrink-0"
      style={{ display: 'inline-block' }}
    />
  );
}

/**
 * Compact language selector dropdown.
 *
 * Props:
 *   align  — 'left' | 'right' (default 'right') — dropdown alignment
 *   upward — boolean (default false) — open upward instead of downward
 */
export default function LanguageSwitcher({ align = 'right', upward = false }) {
  const { language, setLanguage, languages } = useI18n();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const current = languages.find(l => l.code === language) || languages[0];

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = e => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleSelect = code => {
    setLanguage(code);
    setOpen(false);
  };

  const alignClass = align === 'left' ? 'left-0' : 'right-0';
  const positionClass = upward ? 'bottom-full mb-2' : 'top-full mt-2';

  return (
    <div className="relative" ref={ref}>
      {/* Trigger */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl transition-all hover:bg-white/10"
        style={{
          border: '1px solid var(--glass-border)',
          background: open ? 'var(--surface-100)' : 'transparent',
          color: 'var(--text-primary)',
        }}
      >
        <FlagImg code={current.code} size={18} />
        <span className="text-xs font-semibold uppercase tracking-wide hidden sm:inline" style={{ color: 'var(--text-secondary)' }}>
          {current.code}
        </span>
        <motion.span
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className="flex items-center"
          style={{ color: 'var(--text-muted)' }}
        >
          <ChevronDown size={13} />
        </motion.span>
      </button>

      {/* Dropdown */}
      <AnimatePresence>
        {open && (
          <motion.ul
            role="listbox"
            initial={{ opacity: 0, scale: 0.95, y: upward ? 6 : -6 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: upward ? 6 : -6 }}
            transition={{ duration: 0.15 }}
            className={`absolute ${positionClass} ${alignClass} w-44 rounded-2xl overflow-hidden z-[400] shadow-2xl py-1`}
            style={{
              background: 'var(--dropdown-bg)',
              border: '1px solid var(--glass-border)',
            }}
          >
            {languages.map(lang => {
              const active = lang.code === language;
              return (
                <li key={lang.code}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={active}
                    onClick={() => handleSelect(lang.code)}
                    className="w-full flex items-center gap-3 px-3 py-2.5 transition-colors hover:bg-white/10 text-left"
                    style={{ color: active ? 'var(--accent)' : 'var(--text-primary)' }}
                  >
                    <FlagImg code={lang.code} size={20} />
                    <span className="text-sm font-medium flex-1">{lang.label}</span>
                    {active && (
                      <motion.span
                        layoutId="langCheck"
                        className="w-2 h-2 rounded-full flex-shrink-0"
                        style={{ background: 'var(--accent)' }}
                      />
                    )}
                  </button>
                </li>
              );
            })}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
}
